export const ELECTION_TYPES = {
  election: { label: "Election", multiplier: 1.0 },
  by_election: { label: "By Election", multiplier: 1.0 },
  referendum: { label: "Referendum", multiplier: 1.0 },
  ratification: { label: "Ratification", multiplier: 1.0 },
};

export const GOVERNING_REGULATIONS = {
  indian_act: "Indian Act Election Regulations",
  fnea: "First Nation Election Act (FNEA)",
  custom_code: "Custom Code",
  treaty: "Treaty Act / Regulations",
};

// Base fixed inclusions shown in the proposal. The nomination-meeting line is
// only applicable to an Election or By Election.
export const BASE_INCLUSIONS = [
  "1 Electoral Officer",
  "1 Polling Location",
  "Voting Event Administration",
  "Voter Support",
  "Notices, Final Report, and Documentation",
  "Ballots and Polling Location Materials",
  "Electronic Voting",
];
export const NOMINATION_INCLUSION = "1 Nomination Meeting";

export const RATES = {
  baseFixed: 14500,
  includedMembers: 749,
  blockSize: 300,
  blockCost: 1500,
  noticeMailUnit: 1.85,
  ballotMailUnit: 4.85,
  internationalUnit: 19.95,
  additionalPollUnit: 2500,
  byElectionReduction: 0.15,
  byElectionFloor: 12500,
  refRatReduction: 0.2,
  refRatFloor: 11500,
  contingencyPct: 0.03,
  travelMarkup: 0.15,
  taxRate: 0,
};
export const DEFAULT_RATES = RATES;

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

export function membershipBlocks(members, rates = RATES) {
  const over = Math.max(0, (Number(members) || 0) - rates.includedMembers);
  return Math.ceil(over / rates.blockSize);
}

export function calculateEstimate(
  { membershipSize, electionType, voterTurnout, internationalVoters, pollingLocations },
  rates = RATES
) {
  const members = Number(membershipSize) || 0;
  const turnout = Number(voterTurnout) || 0;
  const intlVoters = Math.max(0, Number(internationalVoters) || 0);
  const addlPolls = Math.max(0, (Number(pollingLocations) || 1) - 1);
  const type = ELECTION_TYPES[electionType] || { label: "Custom" };

  const estimatedVoters = Math.round((members * turnout) / 100);
  const domesticVoters = Math.max(0, estimatedVoters - intlVoters);

  const items = [];

  // ---- FIXED BUDGET ----
  items.push({
    name: "Base Fixed Cost (up to 749 members)",
    qty: 1,
    unit: rates.baseFixed,
    amount: rates.baseFixed,
    section: "fixed",
  });

  const blocks = membershipBlocks(members, rates);
  const adjustment = blocks * rates.blockCost;
  if (adjustment > 0) {
    items.push({
      name: "Membership Size Adjustment",
      qty: blocks,
      unit: rates.blockCost,
      amount: adjustment,
      section: "fixed",
    });
  }

  const fixedBeforeReduction = rates.baseFixed + adjustment;
  let reductionAmount = 0;
  if (electionType === "by_election") {
    const target = Math.max(fixedBeforeReduction * (1 - rates.byElectionReduction), rates.byElectionFloor);
    reductionAmount = round2(target - fixedBeforeReduction);
  } else if (electionType === "referendum" || electionType === "ratification") {
    const target = Math.max(fixedBeforeReduction * (1 - rates.refRatReduction), rates.refRatFloor);
    reductionAmount = round2(target - fixedBeforeReduction);
  }
  if (reductionAmount < 0) {
    const reductionRate = electionType === "by_election" ? rates.byElectionReduction : rates.refRatReduction;
    const pct = Math.round(reductionRate * 100);
    items.push({
      name: `${type.label} Adjustment (−${pct}%)`,
      qty: 1,
      unit: reductionAmount,
      amount: reductionAmount,
      section: "fixed",
    });
  }

  // Contingency — a % of the fixed budget (after any election-type reduction).
  const fixedAfterReduction = fixedBeforeReduction + reductionAmount;
  const contingencyPct = rates.contingencyPct == null ? 0.03 : rates.contingencyPct;
  const contingency = round2(fixedAfterReduction * contingencyPct);
  if (contingency !== 0) {
    items.push({
      name: `Contingency (${Math.round(contingencyPct * 100)}%)`,
      qty: 1,
      unit: contingency,
      amount: contingency,
      section: "fixed",
    });
  }

  // ---- VARIABLE BUDGET ----
  items.push({
    name: "Notice Mail-outs",
    qty: domesticVoters,
    unit: rates.noticeMailUnit,
    amount: round2(domesticVoters * rates.noticeMailUnit),
    section: "variable",
  });
  items.push({
    name: "Mail-in Ballot Mail-outs",
    qty: domesticVoters,
    unit: rates.ballotMailUnit,
    amount: round2(domesticVoters * rates.ballotMailUnit),
    section: "variable",
  });
  if (intlVoters > 0) {
    items.push({
      name: "International Voter Mail-outs",
      qty: intlVoters,
      unit: rates.internationalUnit,
      amount: round2(intlVoters * rates.internationalUnit),
      section: "variable",
    });
  }
  if (addlPolls > 0) {
    items.push({
      name: "Additional Polling Locations",
      qty: addlPolls,
      unit: rates.additionalPollUnit,
      amount: round2(addlPolls * rates.additionalPollUnit),
      section: "variable",
    });
  }
  items.push({
    name: "Travel & Travel-Related Costs",
    qty: null,
    unit: null,
    amount: null,
    section: "variable",
    note: "Actual Costs + 15% — invoiced separately; not included in the total below.",
  });

  const totals = computeTotals(items, rates.taxRate);
  return {
    items,
    ...totals,
    estimatedVoters,
    domesticVoters,
    internationalVoters: intlVoters,
    membershipAdjustment: adjustment,
    electionReduction: reductionAmount,
  };
}

export function computeTotals(items, taxRate = 0) {
  const list = items || [];
  let fixedSubtotal = 0;
  let variableSubtotal = 0;
  for (const i of list) {
    if (i.amount == null) continue;
    const amt = Number(i.amount) || 0;
    if (i.section === "fixed") fixedSubtotal += amt;
    else variableSubtotal += amt;
  }
  fixedSubtotal = round2(fixedSubtotal);
  variableSubtotal = round2(variableSubtotal);
  const subtotal = round2(fixedSubtotal + variableSubtotal);
  const rate = Number(taxRate) || 0;
  const tax = round2(subtotal * rate);
  const total = round2(subtotal + tax);
  return { fixedSubtotal, variableSubtotal, subtotal, tax, total, taxRate: rate };
}

export function estimatedVotersFor(membershipSize, voterTurnout) {
  return Math.round((Number(membershipSize) * (Number(voterTurnout) || 0)) / 100);
}

export function formatCurrency(n) {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: 2,
  }).format(Number(n) || 0);
}
